// Create Clip wizard - modal that fires a clip-rule for an entitled match.
//
// Same useful Pipeline knobs as admin (aspect, period, team, duration,
// review, watermark, exclude) but only for this client's account and the
// matches / leagues they already have. Overrides apply to this run only;
// they do not rewrite the saved recipe. Preview is Plan-only.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { listMatches, type PortalMatch } from '../api/portal';
import {
  CLIP_TRANSITIONS,
  listClips,
  listClientClipRules,
  listHighlightFolders,
  previewClipRule,
  runClipRule,
  type ClientClipRule,
  type ClipRuleAspect,
  type ClipRuleOrder,
  type ClipRulePublishMode,
  type ClipRuleTeam,
  type ClipRuleTemplate,
  type ClipTransition,
  type HighlightFolder,
  type PreviewClipRuleResp,
  type RunClipRuleBody,
  type RunClipRuleResp,
} from '../api/clips';
import { ApiError } from '../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultMatchId?: string;
  onQueued?: (reelId: string) => void;
}

function periodsForSport(sport: string): { code: string; label: string }[] {
  if (sport === 'basketball') {
    return [
      { code: 'Q1', label: 'Q1' }, { code: 'Q2', label: 'Q2' },
      { code: 'Q3', label: 'Q3' }, { code: 'Q4', label: 'Q4' },
      { code: 'OT', label: 'OT' },
    ];
  }
  if (sport === 'tennis') {
    return [
      { code: 'set_1', label: 'Set 1' }, { code: 'set_2', label: 'Set 2' },
      { code: 'set_3', label: 'Set 3' }, { code: 'set_4', label: 'Set 4' },
      { code: 'set_5', label: 'Set 5' },
    ];
  }
  return [
    { code: '1H', label: '1st half' },
    { code: '2H', label: '2nd half' },
    { code: 'ET', label: 'Extra time' },
    { code: 'PEN', label: 'Penalties' },
  ];
}

function excludeTypesForSport(sport: string): { code: string; label: string }[] {
  if (sport === 'basketball') {
    return [
      { code: 'foul', label: 'Fouls' },
      { code: 'turnover', label: 'Turnovers' },
      { code: 'free_throw', label: 'Free throws' },
    ];
  }
  if (sport === 'tennis') {
    return [
      { code: 'fault', label: 'Faults' },
      { code: 'let', label: 'Lets' },
    ];
  }
  return [
    { code: 'card', label: 'Cards' },
    { code: 'foul', label: 'Fouls' },
    { code: 'offside', label: 'Offside' },
    { code: 'substitution', label: 'Subs' },
    { code: 'var', label: 'VAR' },
  ];
}

function toggle(list: string[], code: string): string[] {
  return list.includes(code) ? list.filter((c) => c !== code) : [...list, code];
}

function ruleScopeLabel(rule: ClientClipRule): string {
  if (rule.resolved) return 'Resolved for this match';
  if (rule.client_owned && rule.competition_id) return 'Your league recipe';
  if (rule.client_owned) return 'Your account recipe';
  if (rule.competition_id) return 'League default';
  return 'Sport default';
}

function matchWhen(m: PortalMatch): string {
  const raw = (m.scheduled_at || '').slice(0, 10);
  if (!raw) return '';
  const d = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function matchBucket(status: string): 'live' | 'finished' | 'upcoming' {
  if (status === 'finished' || status === 'abandoned' || status === 'cancelled') return 'finished';
  if (status.startsWith('in_play') || status === 'break' || status === 'suspended' || status === 'penalty_shootout') return 'live';
  return 'upcoming';
}

function statusWord(status: string): string {
  const bucket = matchBucket(status);
  if (bucket === 'finished') return status === 'finished' ? 'Finished' : status.replaceAll('_', ' ');
  if (bucket === 'live') return 'Live';
  return 'Scheduled';
}

function Chip({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick}
            className={`rounded-lg px-2.5 py-1 text-xs ring-1 transition ${
              active
                ? 'bg-accent/20 text-white ring-accent/50'
                : 'bg-white/5 text-slate-300 ring-white/10 hover:bg-white/10'
            }`}>
      {children}
    </button>
  );
}

function MatchPicker({
  matches, loading, value, onChange, highlightsOnly,
}: {
  matches: PortalMatch[];
  loading: boolean;
  value: string;
  onChange: (id: string) => void;
  highlightsOnly: boolean;
}) {
  const [q, setQ] = useState('');
  const [league, setLeague] = useState('');
  const [bucket, setBucket] = useState<'all' | 'live' | 'finished' | 'upcoming'>('all');

  const leagues = useMemo(() => {
    const names = new Set<string>();
    for (const m of matches) if (m.competition) names.add(m.competition);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [matches]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return matches.filter((m) => {
      if (league && m.competition !== league) return false;
      if (!highlightsOnly && bucket !== 'all' && matchBucket(m.status) !== bucket) return false;
      if (!needle) return true;
      const blob = `${m.home} ${m.away} ${m.competition} ${m.scheduled_at}`.toLowerCase();
      return blob.includes(needle);
    });
  }, [matches, q, league, bucket, highlightsOnly]);

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label className="label mb-0" htmlFor="highlight-match-search">Match</label>
        <span className="text-[10px] text-subtle">
          {loading ? 'Loading…' : `${filtered.length} shown`}
        </span>
      </div>
      <input
        id="highlight-match-search"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={highlightsOnly ? 'Search finished matches…' : 'Search by team, league, or date…'}
        className="input"
        autoComplete="off"
        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
      />
      {leagues.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip active={league === ''} onClick={() => setLeague('')}>All leagues</Chip>
          {leagues.map((name) => (
            <Chip key={name} active={league === name} onClick={() => setLeague(league === name ? '' : name)}>
              {name}
            </Chip>
          ))}
        </div>
      )}
      {!highlightsOnly && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(['all', 'live', 'finished', 'upcoming'] as const).map((id) => (
            <Chip key={id} active={bucket === id} onClick={() => setBucket(id)}>
              {id === 'all' ? 'All' : id === 'live' ? 'Live' : id === 'finished' ? 'Finished' : 'Scheduled'}
            </Chip>
          ))}
        </div>
      )}
      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl bg-black/20 ring-1 ring-white/10">
        {loading && (
          <p className="px-3 py-6 text-center text-[12px] text-subtle">Loading matches…</p>
        )}
        {!loading && matches.length === 0 && (
          <p className="px-3 py-6 text-center text-[12px] leading-relaxed text-subtle">
            {highlightsOnly
              ? 'No match is ready. Game highlights open after the match is finished and the clipper publishes Full time.'
              : 'No entitled matches yet.'}
          </p>
        )}
        {!loading && matches.length > 0 && filtered.length === 0 && (
          <p className="px-3 py-6 text-center text-[12px] text-subtle">Nothing matches that search.</p>
        )}
        {!loading && filtered.map((m) => {
          const selected = m.id === value;
          const showScore = matchBucket(m.status) !== 'upcoming';
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange(m.id)}
              className={`flex w-full items-center gap-3 border-b border-white/[0.04] px-3 py-2 text-left last:border-b-0 ${
                selected ? 'bg-accent/15' : 'hover:bg-white/[0.04]'
              }`}
            >
              <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${selected ? 'bg-accent' : 'bg-white/15'}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-white">{m.home} vs {m.away}</span>
                <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                  {matchWhen(m)}
                  {m.competition ? ` · ${m.competition}` : ''}
                  {!highlightsOnly ? ` · ${statusWord(m.status)}` : ''}
                </span>
              </span>
              {showScore && (
                <span className="shrink-0 font-mono text-[12px] tabular-nums text-slate-300">
                  {m.score_home}–{m.score_away}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] leading-relaxed text-subtle">
        {highlightsOnly
          ? 'Only finished matches with Full time published by the clipper. A match still in play cannot be turned into this reel.'
          : 'Search narrows the list. Your entitled leagues and matches only.'}
      </p>
    </div>
  );
}

export function CreateClipWizard({ open, onClose, defaultMatchId, onQueued }: Props) {
  const [template, setTemplate] = useState<ClipRuleTemplate>('game_highlights');
  const highlightsOnly = template === 'game_highlights' || template === 'condensed_game';
  const matches = useQuery({
    queryKey: ['portal', 'matches', highlightsOnly ? 'highlights' : 'all'],
    queryFn: () => listMatches(highlightsOnly ? { for: 'highlights' } : undefined),
    enabled: open,
  });
  const folders = useQuery({
    queryKey: ['highlight-folders'],
    queryFn: listHighlightFolders,
    enabled: open,
  });

  const sortedMatches = useMemo(() => {
    const rows = matches.data ?? [];
    const rank = (m: PortalMatch) => {
      if (['finished', 'abandoned'].includes(m.status)) return 0;
      if (['in_play', 'in_play_extra_time', 'break', 'suspended', 'penalty_shootout'].includes(m.status)) return 1;
      return 2;
    };
    return rows.slice().sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      return (b.scheduled_at || '').localeCompare(a.scheduled_at || '');
    });
  }, [matches.data]);

  const [matchId, setMatchId] = useState(defaultMatchId ?? '');
  const [ruleId, setRuleId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [title, setTitle] = useState('');
  const [transition, setTransition] = useState<ClipTransition>('fade');
  const [personId, setPersonId] = useState('');
  const [aspect, setAspect] = useState<ClipRuleAspect>('16:9');
  const [publishMode, setPublishMode] = useState<ClipRulePublishMode>('auto');
  const [team, setTeam] = useState<ClipRuleTeam>('both');
  const [order, setOrder] = useState<ClipRuleOrder>('chronological');
  const [periods, setPeriods] = useState<string[]>([]);
  const [exclude, setExclude] = useState<string[]>([]);
  const [maxDuration, setMaxDuration] = useState(180);
  const [maxClip, setMaxClip] = useState(0);
  const [maxPerTeam, setMaxPerTeam] = useState(0);
  const [watermark, setWatermark] = useState(false);
  const [result, setResult] = useState<RunClipRuleResp | null>(null);
  const [preview, setPreview] = useState<PreviewClipRuleResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Recipe hydrate must not wipe chips the user already clicked (Hold for
  // review before picking a match was getting reset to Straight to folder).
  const touched = useRef({ publishMode: false, aspect: false, team: false, order: false });

  const selectedMatch = useMemo(
    () => sortedMatches.find((m) => m.id === matchId),
    [sortedMatches, matchId],
  );
  const sport = selectedMatch?.sport_code ?? 'football';

  const rules = useQuery({
    queryKey: ['client-clip-rules', template, matchId],
    queryFn: () => listClientClipRules({ template, match_id: matchId }),
    enabled: open && !!matchId,
  });

  const matchClips = useQuery({
    queryKey: ['clips-for-wizard', matchId],
    queryFn: () => listClips({ match_id: matchId, limit: 200 }),
    enabled: open && template === 'player_reel' && !!matchId,
  });
  const players = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of matchClips.data ?? []) {
      if (c.person_id) seen.set(c.person_id, c.person_name ?? 'Player');
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [matchClips.data]);

  const activeRule = useMemo(() => {
    const rows = rules.data ?? [];
    if (ruleId) return rows.find((r) => r.id === ruleId) ?? rows.find((r) => r.resolved) ?? rows[0];
    return rows.find((r) => r.resolved) ?? rows[0];
  }, [rules.data, ruleId]);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setPreview(null);
      setError(null);
      setTitle('');
      setFolderId('');
      setMatchId(defaultMatchId ?? '');
      setTransition('fade');
      setTemplate('game_highlights');
      setPersonId('');
      setRuleId('');
      setAspect('16:9');
      setPublishMode('auto');
      setTeam('both');
      setOrder('chronological');
      setPeriods([]);
      setExclude([]);
      setMaxDuration(180);
      setMaxClip(0);
      setMaxPerTeam(0);
      setWatermark(false);
      touched.current = { publishMode: false, aspect: false, team: false, order: false };
    }
  }, [open, defaultMatchId]);

  useEffect(() => {
    if (!open || matches.isLoading || !matches.isSuccess) return;
    if (matchId && !sortedMatches.some((m) => m.id === matchId)) setMatchId('');
  }, [open, matches.isLoading, matches.isSuccess, matchId, sortedMatches]);

  useEffect(() => {
    if (!activeRule) return;
    const c = activeRule.config;
    if (!touched.current.aspect) setAspect(c.aspect || '16:9');
    if (!touched.current.publishMode) setPublishMode(c.publish_mode || 'auto');
    if (!touched.current.team) {
      setTeam(c.team === 'entitled' && !c.team_id ? 'both' : (c.team || 'both'));
    }
    if (!touched.current.order) setOrder(c.order || 'chronological');
    setPeriods(c.periods ?? []);
    setExclude(c.exclude_event_types ?? []);
    setMaxDuration(c.max_duration_sec || 180);
    setMaxClip(c.max_clip_duration_sec || 0);
    setMaxPerTeam(c.max_per_team || 0);
    setWatermark(!!c.watermark);
    if (c.transition) setTransition(c.transition);
  }, [activeRule?.id]);

  const buildBody = (): RunClipRuleBody => ({
    match_id: matchId,
    rule_id: ruleId || undefined,
    folder_id: folderId || null,
    title: title.trim() || undefined,
    transition,
    person_id: template === 'player_reel' ? personId || undefined : undefined,
    aspect,
    publish_mode: publishMode,
    team,
    order,
    periods,
    exclude_event_types: exclude,
    max_duration_sec: maxDuration,
    max_clip_duration_sec: maxClip,
    max_per_team: maxPerTeam,
    watermark,
  });

  const submit = useMutation({
    mutationFn: () => runClipRule(template, buildBody()),
    onSuccess: (res) => {
      setResult(res);
      if (res.status === 'ok' && res.reel_id && onQueued) onQueued(res.reel_id);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const previewMut = useMutation({
    mutationFn: () => previewClipRule(template, buildBody()),
    onSuccess: (res) => {
      setPreview(res);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (!open) return null;

  const canSubmit = !!matchId && !submit.isPending && (template !== 'player_reel' || !!personId);
  const showEntitled = !!activeRule?.config.team_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
         onClick={onClose}>
      <div className="card flex max-h-[92vh] w-full max-w-2xl flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold text-white">Create highlight</h2>
            <p className="mt-1 text-[13px] text-subtle">Pick a match and recipe. The video lands in Videos.</p>
          </div>
          <button type="button" className="text-[12px] text-subtle hover:text-white" onClick={onClose}>Close</button>
        </div>

        {!result && (
          <form className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4"
                onSubmit={(e) => { e.preventDefault(); if (canSubmit) submit.mutate(); }}>
            <section className="space-y-3">
              <div>
                <label className="label">Template</label>
                <select className="input" value={template}
                        onChange={(e) => {
                          const next = e.target.value as ClipRuleTemplate;
                          const nextFinished = next === 'game_highlights' || next === 'condensed_game';
                          setTemplate(next);
                          setRuleId('');
                          setPreview(null);
                          if (nextFinished !== highlightsOnly) setMatchId('');
                        }}>
                  <option value="game_highlights">Game highlights</option>
                  <option value="goals_only">Goals only</option>
                  <option value="player_reel">Player reel</option>
                  <option value="condensed_game">Condensed game (10 min)</option>
                </select>
              </div>
              <MatchPicker
                key={template}
                matches={sortedMatches}
                loading={matches.isLoading}
                value={matchId}
                highlightsOnly={highlightsOnly}
                onChange={(id) => { setMatchId(id); setRuleId(''); setPreview(null); }}
              />

              {template === 'player_reel' && (
                <div>
                  <label className="label">Player</label>
                  <select className="input" value={personId} required
                          onChange={(e) => setPersonId(e.target.value)}
                          disabled={!matchId || matchClips.isLoading}>
                    <option value="">
                      {!matchId ? 'Pick a match first' : matchClips.isLoading ? 'Loading players…' : 'Choose player…'}
                    </option>
                    {players.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {matchId && (
                <div>
                  <label className="label">Recipe</label>
                  <select className="input" value={ruleId}
                          onChange={(e) => { setRuleId(e.target.value); setPreview(null); }}
                          disabled={rules.isLoading}>
                    <option value="">
                      {activeRule ? `Auto - ${activeRule.name}` : 'Resolved default for this match'}
                    </option>
                    {(rules.data ?? []).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} · {ruleScopeLabel(r)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-subtle">
                    Your account / league recipes plus the sport default. Changing options below does not edit the saved recipe.
                  </p>
                </div>
              )}
            </section>

            <section className={`space-y-3 rounded-xl bg-white/[0.03] p-3 ring-1 ring-white/5 ${
              matchId && rules.isLoading ? 'pointer-events-none opacity-50' : ''
            }`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-subtle">This run</div>

              <div>
                <label className="label">Aspect</label>
                <div className="flex flex-wrap gap-1.5">
                  <Chip active={aspect === '16:9'} onClick={() => { touched.current.aspect = true; setAspect('16:9'); }}>16:9 landscape</Chip>
                  <Chip active={aspect === '9:16'} onClick={() => { touched.current.aspect = true; setAspect('9:16'); }}>9:16 vertical</Chip>
                  <Chip active={aspect === 'both'} onClick={() => { touched.current.aspect = true; setAspect('both'); }}>Both</Chip>
                </div>
                <p className="mt-1 text-[10px] text-subtle">
                  Both queues two files from the same clips. 9:16 cover-crops toward the play.
                </p>
              </div>

              <div>
                <label className="label">Period / half</label>
                <div className="flex flex-wrap gap-1.5">
                  <Chip active={periods.length === 0} onClick={() => setPeriods([])}>Whole match</Chip>
                  {periodsForSport(sport).map((p) => (
                    <Chip key={p.code} active={periods.includes(p.code)}
                          onClick={() => setPeriods((cur) => toggle(cur, p.code))}>
                      {p.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Teams</label>
                <div className="flex flex-wrap gap-1.5">
                  <Chip active={team === 'both'} onClick={() => { touched.current.team = true; setTeam('both'); }}>Both</Chip>
                  <Chip active={team === 'home'} onClick={() => { touched.current.team = true; setTeam('home'); }}>
                    {selectedMatch ? selectedMatch.home : 'Home'}
                  </Chip>
                  <Chip active={team === 'away'} onClick={() => { touched.current.team = true; setTeam('away'); }}>
                    {selectedMatch ? selectedMatch.away : 'Away'}
                  </Chip>
                  {showEntitled && (
                    <Chip active={team === 'entitled'} onClick={() => { touched.current.team = true; setTeam('entitled'); }}>Your club</Chip>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="label">Max length (sec)</label>
                  <input className="input" type="number" min={15} max={600} step={15}
                         value={maxDuration}
                         onChange={(e) => setMaxDuration(Number(e.target.value) || 180)} />
                </div>
                <div>
                  <label className="label">Max per clip (sec)</label>
                  <input className="input" type="number" min={0} max={60} step={1}
                         value={maxClip}
                         onChange={(e) => setMaxClip(Number(e.target.value) || 0)} />
                  <p className="mt-1 text-[10px] text-subtle">0 = clipper IN/OUT</p>
                </div>
                <div>
                  <label className="label">Max per team</label>
                  <input className="input" type="number" min={0} max={40} step={1}
                         value={maxPerTeam}
                         onChange={(e) => setMaxPerTeam(Number(e.target.value) || 0)} />
                  <p className="mt-1 text-[10px] text-subtle">0 = unlimited</p>
                </div>
              </div>

              <div>
                <label className="label">Order</label>
                <div className="flex flex-wrap gap-1.5">
                  <Chip active={order === 'chronological'} onClick={() => { touched.current.order = true; setOrder('chronological'); }}>Match timeline</Chip>
                  <Chip active={order === 'rating'} onClick={() => { touched.current.order = true; setOrder('rating'); }}>Best first</Chip>
                </div>
              </div>

              <div>
                <label className="label">Never include</label>
                <div className="flex flex-wrap gap-1.5">
                  {excludeTypesForSport(sport).map((t) => (
                    <Chip key={t.code} active={exclude.includes(t.code)}
                          onClick={() => setExclude((cur) => toggle(cur, t.code))}>
                      {t.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">After generate</label>
                <div className="flex flex-wrap gap-1.5">
                  <Chip active={publishMode === 'auto'} onClick={() => { touched.current.publishMode = true; setPublishMode('auto'); }}>Straight to folder</Chip>
                  <Chip active={publishMode === 'review'} onClick={() => { touched.current.publishMode = true; setPublishMode('review'); }}>Hold for review</Chip>
                </div>
                <p className="mt-1 text-[10px] text-subtle">
                  {publishMode === 'review'
                    ? 'File still renders and plays here. Publish on the video card before it goes to the site or reel.ready.'
                    : 'Goes live on the site and fires reel.ready as soon as the file is ready.'}
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" checked={watermark}
                       onChange={(e) => setWatermark(e.target.checked)} />
                Origo watermark
              </label>
            </section>

            <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="label">Folder (optional)</label>
                <select className="input" value={folderId}
                        onChange={(e) => setFolderId(e.target.value)}
                        disabled={folders.isLoading}>
                  <option value="">Game Highlights (default)</option>
                  {(folders.data ?? []).filter((f) => f.name.trim().toLowerCase() !== 'game highlights').map((f: HighlightFolder) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Title (optional)</label>
                <input className="input" value={title}
                       placeholder={activeRule?.config.title_template || '{home} vs {away} - Highlights ({date})'}
                       onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Transition between clips</label>
                <select className="input" value={transition}
                        onChange={(e) => setTransition(e.target.value as ClipTransition)}>
                  {CLIP_TRANSITIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-subtle">
                  {CLIP_TRANSITIONS.find((t) => t.value === transition)?.hint}
                </p>
              </div>
            </section>

            {preview && (
              <div className={`rounded-md px-3 py-3 text-sm ${
                preview.status === 'ok'
                  ? 'border border-good/30 bg-good/15 text-emerald-100'
                  : 'border border-warn/30 bg-warn/15 text-amber-100'
              }`}>
                {preview.status === 'ok' ? (
                  <>
                    <div className="font-semibold text-white">
                      {preview.selected_count} clips · {(preview.total_duration_ms / 1000).toFixed(1)}s
                      {preview.aspect === 'both' ? ' · 16:9 + 9:16' : preview.aspect ? ` · ${preview.aspect}` : ''}
                    </div>
                    <div className="mt-1 text-xs opacity-80">{preview.title}</div>
                    <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto text-[11px] opacity-90">
                      {preview.selected.map((s) => (
                        <li key={s.clip_id}>
                          {s.event_type}{s.rating != null ? ` ★${s.rating}` : ''}
                          {s.person_name ? ` · ${s.person_name}` : ''}
                          {s.period ? ` · ${s.period}` : ''}
                          {' · '}{(s.duration_ms / 1000).toFixed(1)}s
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div>
                    <div className="font-semibold text-white">Would not generate</div>
                    <div className="mt-1 text-xs">{preview.reason || 'A guard blocked this plan.'}</div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="rounded-md border border-bad/40 bg-bad/20 px-3 py-2 text-sm text-bad">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn-secondary"
                      disabled={!matchId || previewMut.isPending || (template === 'player_reel' && !personId)}
                      onClick={() => previewMut.mutate()}>
                {previewMut.isPending ? 'Checking…' : 'Preview'}
              </button>
              <button type="submit" className="btn-primary" disabled={!canSubmit}>
                {submit.isPending ? 'Queuing…' : 'Create highlight'}
              </button>
            </div>
          </form>
        )}

        {result && result.status === 'ok' && (
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-md border border-good/30 bg-good/15 px-3 py-3 text-sm text-emerald-200">
              <div className="font-semibold text-white">Highlight queued.</div>
              <div className="mt-1 text-xs">
                “{result.title}” - {(result.duration_ms / 1000).toFixed(1)}s
                {result.vertical_reel_id ? ' · 16:9 + 9:16' : ''}
                {result.review_status === 'pending_review' ? ' · held for review' : ''}.
                It will appear on the Videos page in a few moments.
              </div>
            </div>
            <div className="flex justify-end">
              <button className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        )}

        {result && result.status !== 'ok' && (
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-md border border-warn/30 bg-warn/15 px-3 py-3 text-sm text-amber-100">
              <div className="font-semibold text-white">Highlight not generated.</div>
              <div className="mt-1 text-xs">
                {result.reason || 'A guard on the rule blocked the run.'}
              </div>
              <div className="mt-2 text-[11px] text-amber-100/80">
                Common causes: the match is not finished yet, a required step
                matched nothing, or not enough published clips exist.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => { setResult(null); setPreview(null); }}>
                Adjust and retry
              </button>
              <button className="btn-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
